import React, { useState, useEffect } from 'react';
import api from '../config/api';
import OvertimeModal from '../components/OvertimeModal';
import SearchBar from '../components/SearchBar';
import './ManagementPages.css';

const formatStatus = (status) => {
  if (!status) return '';
  return status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewFilter, setViewFilter] = useState('active'); // 'active' | 'deleted' | 'all'

  const [showIncrementModal, setShowIncrementModal] = useState(false);
  const [incrementType, setIncrementType] = useState('percentage');
  const [incrementValue, setIncrementValue] = useState('');
  const [incrementDept, setIncrementDept] = useState('');
  const [editableEmployees, setEditableEmployees] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyingIncrement, setApplyingIncrement] = useState(false);

  const [showOvertimeModal, setShowOvertimeModal] = useState(false);
  const [overtimeEmployee, setOvertimeEmployee] = useState(null);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [incrementHistory, setIncrementHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [revertingId, setRevertingId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cnic: '',
    address: '',
    position: '',
    department: '',
    dateOfJoining: '',
    salary: '',
    status: 'active',
    bankDetails: {
      accountName: '',
      accountNumber: '',
      bankName: '',
      iban: '',
      swiftCode: ''
    }
  });

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFilter, search, statusFilter]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      if (viewFilter === 'deleted') {
        const response = await api.get('/api/employees/deleted');
        setEmployees(response.data);
      } else if (viewFilter === 'all') {
        const [active, deleted] = await Promise.all([
          api.get('/api/employees', { params: { search, status: statusFilter } }),
          api.get('/api/employees/deleted')
        ]);
        setEmployees([...active.data, ...deleted.data]);
      } else {
        const response = await api.get('/api/employees', {
          params: { search, status: statusFilter }
        });
        setEmployees(response.data);
      }
    } catch (err) {
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id) => {
    if (!window.confirm('Restore this employee?')) return;
    try {
      await api.put(`/api/employees/${id}/restore`);
      fetchEmployees();
    } catch (err) {
      setError('Failed to restore employee');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await api.put(`/api/employees/${editingEmployee._id}`, formData);
      } else {
        await api.post('/api/employees', formData);
      }
      fetchEmployees();
      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save employee');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await api.delete(`/api/employees/${id}`);
        fetchEmployees();
      } catch (err) {
        setError('Failed to delete employee');
      }
    }
  };

  const openModal = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        ...employee,
        dateOfJoining: employee.dateOfJoining?.split('T')[0]
      });
    } else {
      setEditingEmployee(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        cnic: '',
        address: '',
        position: '',
        department: '',
        dateOfJoining: '',
        salary: '',
        status: 'active',
        bankDetails: {
          accountName: '',
          accountNumber: '',
          bankName: '',
          iban: '',
          swiftCode: ''
        }
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEmployee(null);
    setError('');
  };

  const openIncrementModal = () => {
    setIncrementType('percentage');
    setIncrementValue('');
    setIncrementDept('');
    setEditableEmployees([]);
    setShowIncrementModal(true);
  };

  const closeIncrementModal = () => {
    setShowIncrementModal(false);
    setEditableEmployees([]);
  };

  const handleIncrementPreview = async () => {
    const val = parseFloat(incrementValue);
    if (!val || val <= 0) return;
    setPreviewLoading(true);
    try {
      const response = await api.post('/api/employees/increment/preview', {
        type: incrementType,
        value: val,
        department: incrementDept || undefined
      });
      setEditableEmployees(response.data.employees.map(e => ({ ...e })));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleIncreaseEdit = (id, rawValue) => {
    const increase = parseFloat(rawValue) || 0;
    setEditableEmployees(prev => prev.map(emp =>
      emp._id === id
        ? { ...emp, increase, newSalary: emp.currentSalary + increase }
        : emp
    ));
  };

  const editableSummary = {
    totalCurrent: editableEmployees.reduce((s, e) => s + e.currentSalary, 0),
    totalIncrease: editableEmployees.reduce((s, e) => s + e.increase, 0),
    totalNew: editableEmployees.reduce((s, e) => s + e.newSalary, 0),
  };

  const handleIncrementApply = async () => {
    if (!editableEmployees.length) return;
    const val = parseFloat(incrementValue);
    const label = incrementType === 'percentage' ? `${val}%` : `PKR ${val.toFixed(2)}`;
    if (!window.confirm(`Apply ${label} increment to ${editableEmployees.length} employees? This will update their base salaries.`)) return;
    setApplyingIncrement(true);
    try {
      const response = await api.post('/api/employees/increment/apply', {
        type: incrementType,
        value: val,
        department: incrementDept || undefined,
        employees: editableEmployees.map(e => ({ _id: e._id, newSalary: e.newSalary }))
      });
      alert(response.data.message);
      fetchEmployees();
      closeIncrementModal();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to apply increment');
    } finally {
      setApplyingIncrement(false);
    }
  };

  const departments = [...new Set(employees.filter(e => !e.isDeleted).map(e => e.department).filter(Boolean))];

  const openHistoryModal = async () => {
    setShowHistoryModal(true);
    setHistoryLoading(true);
    try {
      const response = await api.get('/api/employees/increment/history');
      setIncrementHistory(response.data);
    } catch (err) {
      setError('Failed to load increment history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRevert = async (record) => {
    const label = record.type === 'percentage' ? `${record.value}%` : `PKR ${record.value}`;
    const dept = record.department ? ` (${record.department})` : '';
    if (!window.confirm(`Revert ${label}${dept} increment applied on ${new Date(record.createdAt).toLocaleDateString()}? Employee salaries will be restored to their previous values.`)) return;
    setRevertingId(record._id);
    try {
      const response = await api.post(`/api/employees/increment/revert/${record._id}`);
      alert(response.data.message);
      fetchEmployees();
      // Refresh history list
      const updated = await api.get('/api/employees/increment/history');
      setIncrementHistory(updated.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to revert increment');
    } finally {
      setRevertingId(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('bank.')) {
      const bankField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        bankDetails: {
          ...prev.bankDetails,
          [bankField]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Employee Management</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={openHistoryModal} style={{ backgroundColor: '#4a3a1a', borderColor: '#4a3a1a' }}>
            Increment History
          </button>
          <button className="btn btn-secondary" onClick={openIncrementModal} style={{ backgroundColor: '#1a7a4a', borderColor: '#1a7a4a' }}>
            Increment Salaries
          </button>
          <button className="btn btn-primary" onClick={() => openModal()}>
            + Add Employee
          </button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="filters">
        <SearchBar
          placeholder="Search employees..."
          onSearch={setSearch}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          disabled={viewFilter === 'deleted'}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="on-leave">On Leave</option>
        </select>
        <select
          value={viewFilter}
          onChange={(e) => setViewFilter(e.target.value)}
        >
          <option value="active">Active employees</option>
          <option value="all">All (incl. deleted)</option>
          <option value="deleted">Deleted only</option>
        </select>
      </div>

      <div className="table-container">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Position</th>
              <th>Department</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Salary</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.map(employee => (
              <tr key={employee._id} style={employee.isDeleted ? { opacity: 0.6 } : {}}>
                <td>
                  {employee.name}
                  {employee.isDeleted && (
                    <span style={{ marginLeft: '8px', fontSize: '11px', background: '#721c24', color: '#fff', padding: '2px 6px', borderRadius: '4px' }}>
                      Deleted
                    </span>
                  )}
                </td>
                <td>{employee.position}</td>
                <td>{employee.department}</td>
                <td>{employee.email}</td>
                <td>{employee.phone}</td>
                <td>PKR {employee.salary?.toFixed(2)}</td>
                <td>
                  {employee.isDeleted ? (
                    <span style={{ fontSize: '12px', color: '#aaa' }}>
                      {employee.deletedAt ? new Date(employee.deletedAt).toLocaleDateString() : '—'}
                    </span>
                  ) : (
                    <span className={`status-badge status-${employee.status}`}>
                      {formatStatus(employee.status)}
                    </span>
                  )}
                </td>
                <td>
                  {employee.isDeleted ? (
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleRestore(employee._id)}
                      style={{ fontSize: '12px', padding: '4px 10px' }}
                    >
                      Restore
                    </button>
                  ) : (
                    <>
                      <button
                        className="btn-icon"
                        onClick={() => { setOvertimeEmployee(employee); setShowOvertimeModal(true); }}
                        title="Log Overtime"
                        style={{ fontSize: '16px' }}
                      >
                        ⏱
                      </button>
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => openModal(employee)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDelete(employee._id)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {showHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '720px', width: '95%' }}>
            <div className="modal-header">
              <h2>Increment History</h2>
              <button className="close-btn" onClick={() => setShowHistoryModal(false)}>×</button>
            </div>

            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: '30px', color: '#aaa' }}>Loading...</div>
            ) : incrementHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px', color: '#aaa' }}>No increment history yet.</div>
            ) : (
              <div style={{ maxHeight: '480px', overflowY: 'auto' }}>
                {incrementHistory.map(record => {
                  const label = record.type === 'percentage' ? `${record.value}%` : `PKR ${Number(record.value).toFixed(2)}`;
                  const dept = record.department ? ` — ${record.department}` : ' — All Departments';
                  const totalOld = record.employees.reduce((s, e) => s + e.oldSalary, 0);
                  const totalNew = record.employees.reduce((s, e) => s + e.newSalary, 0);
                  return (
                    <div key={record._id} style={{ border: '1px solid #333', borderRadius: '8px', padding: '14px', marginBottom: '12px', opacity: record.reverted ? 0.6 : 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                        <div>
                          <span style={{ fontWeight: 'bold', fontSize: '15px', color: record.reverted ? '#aaa' : '#4fc3f7' }}>{label}</span>
                          <span style={{ fontSize: '12px', color: '#aaa', marginLeft: '10px' }}>{dept}</span>
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                            Applied {new Date(record.createdAt).toLocaleString()} · {record.employees.length} employees
                            {record.reverted && <span style={{ marginLeft: '8px', color: '#e74c3c' }}>· Reverted {new Date(record.revertedAt).toLocaleDateString()}</span>}
                          </div>
                        </div>
                        {!record.reverted && (
                          <button
                            className="btn btn-danger"
                            style={{ fontSize: '12px', padding: '4px 12px' }}
                            onClick={() => handleRevert(record)}
                            disabled={revertingId === record._id}
                          >
                            {revertingId === record._id ? 'Reverting...' : 'Revert'}
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '12px', marginTop: '10px', flexWrap: 'wrap' }}>
                        <div style={{ fontSize: '12px', color: '#aaa' }}>
                          Before: <span style={{ color: '#fff' }}>PKR {totalOld.toFixed(2)}</span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#aaa' }}>
                          After: <span style={{ color: '#4fc3f7' }}>PKR {totalNew.toFixed(2)}</span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#aaa' }}>
                          Increase: <span style={{ color: '#4caf50' }}>+ PKR {(totalNew - totalOld).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {showIncrementModal && (
        <div className="modal-overlay" onClick={closeIncrementModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '95%' }}>
            <div className="modal-header">
              <h2>Increment Salaries</h2>
              <button className="close-btn" onClick={closeIncrementModal}>×</button>
            </div>

            <div className="form-row" style={{ marginBottom: '16px' }}>
              <div className="form-group">
                <label>Increment Type</label>
                <select value={incrementType} onChange={(e) => { setIncrementType(e.target.value); setEditableEmployees([]); }}>
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (PKR)</option>
                </select>
              </div>
              <div className="form-group">
                <label>{incrementType === 'percentage' ? 'Percentage (%)' : 'Amount (PKR)'}</label>
                <input
                  type="number"
                  min="0"
                  step={incrementType === 'percentage' ? '0.1' : '1'}
                  value={incrementValue}
                  onChange={(e) => { setIncrementValue(e.target.value); setEditableEmployees([]); }}
                  placeholder={incrementType === 'percentage' ? 'e.g. 10' : 'e.g. 5000'}
                />
              </div>
              <div className="form-group">
                <label>Department (optional)</label>
                <select value={incrementDept} onChange={(e) => { setIncrementDept(e.target.value); setEditableEmployees([]); }}>
                  <option value="">All Departments</option>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <button
                className="btn btn-secondary"
                onClick={handleIncrementPreview}
                disabled={!incrementValue || parseFloat(incrementValue) <= 0 || previewLoading}
              >
                {previewLoading ? 'Loading...' : 'Preview Impact'}
              </button>
            </div>

            {editableEmployees.length > 0 && (
              <>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <div style={{ background: '#1e1e2e', borderRadius: '8px', padding: '12px 20px', flex: 1, minWidth: '140px' }}>
                    <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>Current Total</div>
                    <div style={{ fontWeight: 'bold', fontSize: '15px' }}>PKR {editableSummary.totalCurrent.toFixed(2)}</div>
                  </div>
                  <div style={{ background: '#1e2e1e', borderRadius: '8px', padding: '12px 20px', flex: 1, minWidth: '140px' }}>
                    <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>Total Increase</div>
                    <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#4caf50' }}>+ PKR {editableSummary.totalIncrease.toFixed(2)}</div>
                  </div>
                  <div style={{ background: '#1a2a3a', borderRadius: '8px', padding: '12px 20px', flex: 1, minWidth: '140px' }}>
                    <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>New Total</div>
                    <div style={{ fontWeight: 'bold', fontSize: '15px', color: '#4fc3f7' }}>PKR {editableSummary.totalNew.toFixed(2)}</div>
                  </div>
                </div>

                <div style={{ maxHeight: '280px', overflowY: 'auto', marginBottom: '16px' }}>
                  <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#1e1e2e' }}>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #333' }}>Employee</th>
                        <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #333' }}>Department</th>
                        <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #333' }}>Current</th>
                        <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #333', color: '#4caf50' }}>Increase (editable)</th>
                        <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #333', color: '#4fc3f7' }}>New Salary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editableEmployees.map(emp => (
                        <tr key={emp._id} style={{ borderBottom: '1px solid #2a2a2a' }}>
                          <td style={{ padding: '7px 8px' }}>{emp.name}</td>
                          <td style={{ padding: '7px 8px', color: '#aaa', fontSize: '12px' }}>{emp.department}</td>
                          <td style={{ padding: '7px 8px', textAlign: 'right' }}>PKR {emp.currentSalary.toFixed(2)}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                            <input
                              type="number"
                              value={emp.increase}
                              onChange={(e) => handleIncreaseEdit(emp._id, e.target.value)}
                              style={{
                                width: '110px', textAlign: 'right', background: '#1e2e1e',
                                border: '1px solid #4caf50', borderRadius: '4px',
                                color: '#4caf50', padding: '3px 6px', fontSize: '13px'
                              }}
                            />
                          </td>
                          <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 'bold', color: '#4fc3f7' }}>PKR {emp.newSalary.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={closeIncrementModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleIncrementApply}
                    disabled={applyingIncrement}
                    style={{ backgroundColor: '#1a7a4a', borderColor: '#1a7a4a' }}
                  >
                    {applyingIncrement ? 'Applying...' : `Apply to ${editableEmployees.length} Employees`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <OvertimeModal
        show={showOvertimeModal}
        onClose={() => setShowOvertimeModal(false)}
        employees={employees.filter(e => !e.isDeleted)}
        defaultEmployeeId={overtimeEmployee?._id}
        onSaved={() => {}}
      />

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone *</label>
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>CNIC *</label>
                  <input
                    name="cnic"
                    value={formData.cnic}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Position *</label>
                  <input
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Department *</label>
                  <input
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date of Joining *</label>
                  <input
                    type="date"
                    name="dateOfJoining"
                    value={formData.dateOfJoining}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Salary *</label>
                  <input
                    type="number"
                    name="salary"
                    value={formData.salary}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Status *</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="on-leave">On Leave</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Address *</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows="3"
                />
              </div>

              <h3>Bank Details</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Account Name</label>
                  <input
                    name="bank.accountName"
                    value={formData.bankDetails.accountName}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Account Number</label>
                  <input
                    name="bank.accountNumber"
                    value={formData.bankDetails.accountNumber}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Bank Name</label>
                  <input
                    name="bank.bankName"
                    value={formData.bankDetails.bankName}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>IBAN</label>
                  <input
                    name="bank.iban"
                    value={formData.bankDetails.iban}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Swift Code</label>
                <input
                  name="bank.swiftCode"
                  value={formData.bankDetails.swiftCode}
                  onChange={handleChange}
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingEmployee ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;
